import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Plus, ChevronDown, ChevronUp, Phone } from 'lucide-react';
import BottomNav from '../components/BottomNav';

const LEVEL_CONFIG = {
  1: { color: '#15803d', label: 'Monitoring',      border: '#16a34a' },
  2: { color: '#b45309', label: 'Notable',          border: '#d97706' },
  3: { color: '#c2410c', label: 'Emerging Pattern', border: '#ea580c' },
  4: { color: '#b91c1c', label: 'High Risk',        border: '#dc2626' },
  5: { color: '#7f1d1d', label: 'Critical Threat',  border: '#991b1b' },
};

const formatDate = (d) => {
  if (!d) return 'Unknown';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
const formatDateTime = (d) => {
  if (!d) return 'Unknown';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

function SightingCard({ sighting, index, dark }) {
  const [panel, setPanel] = useState(0);
  const touchStartX = useRef(null);

  const cardBg = dark ? 'rgba(0,0,0,0.35)' : '#f7f8fc';
  const cardBorder = dark ? 'rgba(0,0,0,0.4)' : '#e4e8f0';
  const titleColor = dark ? 'white' : '#001a4d';
  const subColor = dark ? 'rgba(255,255,255,0.55)' : '#7a8aaa';
  const tabActive = dark ? 'rgba(0,0,0,0.5)' : '#001a4d';
  const tabActiveText = 'white';
  const tabInactive = dark ? 'rgba(0,0,0,0.25)' : '#e4e8f0';
  const tabInactiveText = dark ? 'rgba(255,255,255,0.4)' : '#7a8aaa';
  const dotActive = dark ? 'white' : '#001a4d';
  const dotInactive = dark ? 'rgba(255,255,255,0.2)' : '#d0d8e8';

  const panels = [
    {
      label: 'What',
      content: (
        <div>
          <div style={{ fontSize: '13px', color: titleColor, fontWeight: 700, marginBottom: '6px' }}>
            {sighting.category_label}
          </div>
          {sighting.evidence_source && (
            <div style={{ fontSize: '11px', color: subColor, lineHeight: 1.5 }}>
              {sighting.evidence_source}
            </div>
          )}
        </div>
      ),
    },
    {
      label: 'When & Where',
      content: (
        <div>
          <div style={{ fontSize: '12px', color: titleColor, fontWeight: 700, marginBottom: '4px' }}>
            {formatDateTime(sighting.date)}
          </div>
          <div style={{ fontSize: '12px', color: subColor }}>
            {sighting.region || 'Region unknown'}
          </div>
        </div>
      ),
    },
    {
      label: 'Police',
      content: sighting.reported_to_police ? (
        <div style={{
          background: dark ? 'rgba(201,151,58,0.15)' : '#fffbf0',
          border: dark ? '1px solid rgba(201,151,58,0.3)' : '1px solid #f0d890',
          borderLeft: '3px solid #C9973A',
          borderRadius: '6px', padding: '8px 10px', width: '100%',
        }}>
          <div style={{ fontSize: '9px', fontFamily: "'Oswald', sans-serif", fontWeight: 600, letterSpacing: '2px', color: '#C9973A', textTransform: 'uppercase', marginBottom: '4px' }}>
            Reported
          </div>
          {sighting.reported_to_force && (
            <div style={{ fontSize: '12px', color: dark ? 'white' : '#001a4d', fontWeight: 700, marginBottom: '2px' }}>
              {sighting.reported_to_force}
            </div>
          )}
          {sighting.police_incident_number && (
            <div style={{ fontSize: '11px', color: subColor, fontFamily: "'Oswald', sans-serif", letterSpacing: '1px' }}>
              Inc: {sighting.police_incident_number}
            </div>
          )}
        </div>
      ) : (
        <div style={{ fontSize: '12px', color: subColor, fontStyle: 'italic' }}>
          Not reported to police
        </div>
      ),
    },
  ];

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (diff > 40 && panel < panels.length - 1) setPanel(panel + 1);
    if (diff < -40 && panel > 0) setPanel(panel - 1);
    touchStartX.current = null;
  };

  return (
    <div style={{
      background: cardBg, border: `1.5px solid ${cardBorder}`,
      borderRadius: '10px', padding: '12px 14px',
      marginBottom: '10px', userSelect: 'none',
    }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      data-testid={`sighting-${index}`}
    >
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
        {panels.map((p, i) => (
          <button key={i} onClick={() => setPanel(i)} style={{
            flex: 1, padding: '5px 4px', border: 'none', borderRadius: '6px',
            fontFamily: "'Oswald', sans-serif", fontSize: '9px', fontWeight: 600,
            letterSpacing: '1.5px', textTransform: 'uppercase', cursor: 'pointer',
            background: panel === i ? tabActive : tabInactive,
            color: panel === i ? tabActiveText : tabInactiveText,
            transition: 'all 0.15s',
          }}>{p.label}</button>
        ))}
      </div>
      <div style={{ minHeight: '54px' }}>{panels[panel].content}</div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '5px', marginTop: '10px' }}>
        {panels.map((_, i) => (
          <div key={i} style={{
            width: panel === i ? '16px' : '6px', height: '6px', borderRadius: '3px',
            background: panel === i ? dotActive : dotInactive, transition: 'all 0.2s',
          }} />
        ))}
      </div>
    </div>
  );
}

export default function MatchResultScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { result, vrn } = location.state || {};

  if (!result || !result.match) { navigate('/', { replace: true }); return null; }

  const level = result.threat_level || 1;
  const config = LEVEL_CONFIG[level] || LEVEL_CONFIG[1];

  const handleCheckAnother = () => navigate('/', { replace: true });
  const handleLogSighting = () => navigate('/log-sighting', { state: { vrn: vrn || result.vrn } });

  if (level >= 4) {
    return <HighAlertScreen result={result} vrn={vrn} level={level} config={config}
      onLogSighting={handleLogSighting} onCheckAnother={handleCheckAnother} />;
  }

  return <IntelligenceScreen result={result} vrn={vrn} level={level} config={config}
    onCheckAnother={handleCheckAnother} onLogSighting={handleLogSighting} />;
}

function IntelligenceScreen({ result, vrn, level, config, onCheckAnother, onLogSighting }) {
  const [showAll, setShowAll] = useState(false);
  const allSightings = result.sightings || [];
  const displayed = showAll ? allSightings : allSightings.slice(0, 5);

  return (
    <div style={{
      minHeight: '100vh', background: '#001a4d',
      display: 'flex', flexDirection: 'column',
      fontFamily: "'Inter', sans-serif", paddingBottom: '80px',
    }} data-testid="intelligence-screen">

      <div style={{
        background: '#001a4d', padding: '48px 20px 28px',
        textAlign: 'center', borderBottom: `3px solid ${config.border}`,
        position: 'relative',
      }}>
        <div style={{ position: 'absolute', top: '16px', left: '16px' }}>
          <button onClick={onCheckAnother} style={{
            width: '38px', height: '38px', background: 'rgba(255,255,255,0.1)',
            border: 'none', borderRadius: '10px', display: 'flex',
            alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }} data-testid="back-btn"><ArrowLeft size={18} color="white" /></button>
        </div>
        <div style={{
          background: '#F5C400', border: '3px solid #111', borderRadius: '8px',
          display: 'inline-block', padding: '10px 24px', marginBottom: '14px',
          fontFamily: "'Oswald', sans-serif", fontSize: '30px',
          fontWeight: 700, color: '#111', letterSpacing: '7px',
        }} data-testid="result-vrn">{vrn || result.vrn}</div>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
          <span style={{
            background: config.color, color: 'white', borderRadius: '6px', padding: '6px 16px',
            fontFamily: "'Oswald', sans-serif", fontSize: '13px',
            fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase',
          }}>Level {level} — {result.threat_label || config.label}</span>
        </div>
        <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: '13px', color: 'rgba(255,255,255,0.55)', letterSpacing: '1px' }}>
          {result.action_required || 'No immediate action required'}
        </div>
      </div>

      <div style={{ flex: 1, padding: '16px', overflowY: 'auto' }}>
        {result.hard_flag && (
          <div style={{
            background: '#fff5f5', border: '1.5px solid #fecaca',
            borderLeft: '4px solid #dc2626', borderRadius: '10px',
            padding: '12px 14px', marginBottom: '12px',
            fontSize: '12px', color: '#dc2626', fontWeight: 600,
          }} data-testid="hard-flag-banner">⚠ {result.hard_flag_reason}</div>
        )}
        {level === 3 && (
          <a href="tel:101" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '8px', width: '100%', padding: '14px',
            background: '#c2410c', border: 'none', borderRadius: '10px',
            color: 'white', fontFamily: "'Oswald', sans-serif",
            fontSize: '17px', fontWeight: 600, letterSpacing: '3px',
            textTransform: 'uppercase', textDecoration: 'none', marginBottom: '12px',
          }} data-testid="call-101-btn"><Phone size={18} /> Call 101</a>
        )}
        <div style={{
          background: 'white', borderRadius: '12px', padding: '16px',
          marginBottom: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
          borderLeft: `4px solid ${config.border}`,
        }}>
          <div style={{
            fontFamily: "'Oswald', sans-serif", fontSize: '10px', fontWeight: 600,
            letterSpacing: '3px', color: '#4a5a7a', textTransform: 'uppercase',
            marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #eef1f7',
          }}>Intelligence Summary</div>
          {[
            ['👁 National Sightings', result.national_sightings],
            ['📅 First Seen', formatDate(result.first_seen)],
            ['🕐 Last Seen', formatDate(result.last_seen)],
            ['📍 Region', result.regions?.slice(0,3).join(', ') || 'Unknown'],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}>
              <span style={{ fontSize: '11px', color: '#7a8aaa' }}>{k}</span>
              <span style={{ fontSize: '12px', color: '#001a4d', fontWeight: 700 }}>{v}</span>
            </div>
          ))}
        </div>
        {allSightings.length > 0 && (
          <div style={{
            background: 'white', borderRadius: '12px', padding: '16px',
            marginBottom: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
          }}>
            <div style={{
              fontFamily: "'Oswald', sans-serif", fontSize: '10px', fontWeight: 600,
              letterSpacing: '3px', color: '#4a5a7a', textTransform: 'uppercase',
              marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #eef1f7',
            }}>Previous Sightings</div>
            {displayed.map((s, i) => <SightingCard key={i} sighting={s} index={i} dark={false} />)}
            {allSightings.length > 5 && (
              <button onClick={() => setShowAll(!showAll)} style={{
                width: '100%', padding: '10px', background: 'rgba(0,26,77,0.06)',
                border: 'none', borderRadius: '8px', color: '#001a4d', cursor: 'pointer',
                fontFamily: "'Oswald', sans-serif", fontSize: '12px', fontWeight: 600,
                letterSpacing: '2px', textTransform: 'uppercase',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              }} data-testid="toggle-sightings-btn">
                {showAll ? <><ChevronUp size={14} /> Show Less</> : <><ChevronDown size={14} /> Show All ({allSightings.length})</>}
              </button>
            )}
          </div>
        )}
        <p style={{ textAlign: 'center', fontSize: '10px', color: 'rgba(255,255,255,0.35)', marginBottom: '16px', letterSpacing: '0.5px' }}>
          If in doubt, call 999.
        </p>
        <button onClick={onLogSighting} style={{
          width: '100%', background: 'white', border: 'none', borderRadius: '10px',
          padding: '15px', color: '#001a4d', fontFamily: "'Oswald', sans-serif",
          fontSize: '16px', fontWeight: 600, letterSpacing: '4px', textTransform: 'uppercase',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '8px', cursor: 'pointer', marginBottom: '10px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
        }} data-testid="log-sighting-btn">
          <Plus size={18} /><span>Log a Sighting</span>
        </button>
        <button onClick={onCheckAnother} style={{
          width: '100%', background: 'transparent',
          border: '2px solid rgba(255,255,255,0.3)', borderRadius: '10px',
          padding: '13px', color: 'rgba(255,255,255,0.8)',
          fontFamily: "'Oswald', sans-serif", fontSize: '16px',
          fontWeight: 600, letterSpacing: '3px', textTransform: 'uppercase', cursor: 'pointer',
        }} data-testid="check-another-btn">Check Another</button>
      </div>

      <BottomNav activeTab="home" />
    </div>
  );
}

function HighAlertScreen({ result, vrn, level, config, onLogSighting, onCheckAnother }) {
  const [showIntel, setShowIntel] = useState(false);
  const allSightings = result.sightings || [];

  React.useEffect(() => {
    if (navigator.vibrate) navigator.vibrate(level === 5 ? [500, 200, 500] : 500);
    if (level === 5) {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = 880; osc.type = 'square';
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.3);
      } catch (e) {}
    }
  }, [level]);

  const bg = level === 5 ? '#7f1d1d' : '#991b1b';

  return (
    <div style={{
      minHeight: '100vh', background: bg,
      display: 'flex', flexDirection: 'column',
      fontFamily: "'Inter', sans-serif",
      position: 'relative', overflowY: 'auto',
    }} data-testid={`level${level}-alert-screen`}>

      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: '6px',
        background: 'repeating-linear-gradient(90deg, #F5C400 0, #F5C400 20px, #111 20px, #111 40px)',
      }} />

      {/* Hero section */}
      <div style={{
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '48px 20px 28px', textAlign: 'center',
      }}>
        {result.hard_flag && (
          <div style={{
            background: 'rgba(255,255,255,0.12)', borderRadius: '10px',
            padding: '12px 16px', marginBottom: '20px',
            fontSize: '12px', color: 'white', fontWeight: 600,
            width: '100%', maxWidth: '360px',
          }} data-testid="hard-flag-banner">⚠ {result.hard_flag_reason}</div>
        )}
        <div style={{
          fontFamily: "'Oswald', sans-serif", fontSize: '11px', fontWeight: 600,
          letterSpacing: '4px', color: 'rgba(255,255,255,0.6)',
          textTransform: 'uppercase', marginBottom: '4px',
        }}>⚠ High Threat Vehicle Detected</div>
        <div style={{
          fontFamily: "'Oswald', sans-serif", fontSize: '22px', fontWeight: 700,
          color: 'white', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '4px',
        }}>Call Police Now</div>
        <div style={{
          fontFamily: "'Oswald', sans-serif", fontSize: '64px', fontWeight: 700,
          color: 'white', letterSpacing: '8px', lineHeight: 1, marginBottom: '16px',
        }}>999</div>
        <div style={{
          background: '#F5C400', border: '3px solid #111', borderRadius: '8px',
          padding: '10px 24px', fontFamily: "'Oswald', sans-serif",
          fontSize: '28px', fontWeight: 700, color: '#111',
          letterSpacing: '7px', marginBottom: '12px',
        }} data-testid="alert-vrn">{vrn || result.vrn}</div>
        <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '12px', marginBottom: '2px' }}>
          National Sightings: <strong style={{ color: 'white' }}>{result.national_sightings}</strong>
        </div>
        {result.regions?.length > 0 && (
          <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '12px', marginBottom: '4px' }}>
            Region: <strong style={{ color: 'white' }}>{result.regions.slice(0,3).join(', ')}</strong>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div style={{ padding: '0 20px', width: '100%', maxWidth: '400px', margin: '0 auto' }}>
        <a href="tel:999" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '100%', padding: '18px', background: 'white', borderRadius: '12px',
          fontFamily: "'Oswald', sans-serif", fontSize: '22px', fontWeight: 700,
          color: bg, letterSpacing: '4px', textDecoration: 'none',
          marginBottom: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }} data-testid="call-999-btn">📞 CALL 999</a>

        <button onClick={onLogSighting} style={{
          width: '100%', background: 'transparent',
          border: '2px solid rgba(255,255,255,0.5)', borderRadius: '10px',
          padding: '13px', color: 'white', fontFamily: "'Oswald', sans-serif",
          fontSize: '15px', fontWeight: 600, letterSpacing: '3px', textTransform: 'uppercase',
          cursor: 'pointer', display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: '8px', marginBottom: '10px',
        }} data-testid="log-sighting-btn"><Plus size={16} /> Log a Sighting</button>

        <button onClick={onCheckAnother} style={{
          width: '100%', background: 'transparent',
          border: '2px solid rgba(255,255,255,0.3)', borderRadius: '10px',
          padding: '13px', color: 'rgba(255,255,255,0.7)',
          fontFamily: "'Oswald', sans-serif", fontSize: '15px',
          fontWeight: 600, letterSpacing: '3px', textTransform: 'uppercase',
          cursor: 'pointer', marginBottom: '16px',
        }} data-testid="check-another-btn">Check Another</button>

        {/* View Intelligence toggle */}
        <button onClick={() => setShowIntel(!showIntel)} style={{
          width: '100%', background: 'rgba(0,0,0,0.25)',
          border: '1.5px solid rgba(255,255,255,0.2)', borderRadius: '10px',
          padding: '12px', color: 'white', fontFamily: "'Oswald', sans-serif",
          fontSize: '13px', fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase',
          cursor: 'pointer', display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: '8px', marginBottom: '16px',
        }}>
          {showIntel ? <><ChevronUp size={14} /> Hide Intelligence</> : <><ChevronDown size={14} /> View Intelligence ({allSightings.length} sightings)</>}
        </button>

        {/* Collapsible intel section */}
        {showIntel && (
          <div style={{ marginBottom: '24px' }}>
            {/* Summary */}
            <div style={{
              background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,0,0,0.4)',
              borderRadius: '12px', padding: '14px', marginBottom: '12px',
            }}>
              <div style={{
                fontFamily: "'Oswald', sans-serif", fontSize: '10px', fontWeight: 600,
                letterSpacing: '3px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase',
                marginBottom: '10px', paddingBottom: '8px',
                borderBottom: '1px solid rgba(255,255,255,0.1)',
              }}>Intelligence Summary</div>
              {[
                ['👁 National Sightings', result.national_sightings],
                ['📅 First Seen', formatDate(result.first_seen)],
                ['🕐 Last Seen', formatDate(result.last_seen)],
                ['📍 Region', result.regions?.slice(0,3).join(', ') || 'Unknown'],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>{k}</span>
                  <span style={{ fontSize: '12px', color: 'white', fontWeight: 700 }}>{v}</span>
                </div>
              ))}
            </div>

            {/* Sighting cards */}
            {allSightings.length > 0 && (
              <div>
                <div style={{
                  fontFamily: "'Oswald', sans-serif", fontSize: '10px', fontWeight: 600,
                  letterSpacing: '3px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase',
                  marginBottom: '10px',
                }}>Previous Sightings</div>
                {allSightings.map((s, i) => <SightingCard key={i} sighting={s} index={i} dark={true} />)}
              </div>
            )}
          </div>
        )}

        <p style={{ textAlign: 'center', fontSize: '10px', color: 'rgba(255,255,255,0.35)', paddingBottom: '24px' }}>
          Do not approach alone · Await police response
        </p>
      </div>
    </div>
  );
}
